// components/views/Favorites.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';
import FavoritesService from '../../services/favorites';
import ListenModal from '../ui/ListenModal';
import '../../styles/favorites.css';

const Favorites = () => {
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { refreshFavorites } = useApp();
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGenre, setSelectedGenre] = useState(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(null); // artistId da confermare
    const [sortBy, setSortBy] = useState('date'); // 'date' o 'name'
    const [openMenuId, setOpenMenuId] = useState(null);
    const [listenModal, setListenModal] = useState({ isOpen: false, artistName: null, ids: {}, mode: 'listen' });

    // Carica preferiti (solo una volta, ordinamento client-side)
    useEffect(() => {
        const loadFavorites = async () => {
            try {
                setLoading(true);
                const data = await FavoritesService.getUserFavorites();
                setFavorites(data);
            } catch (error) {
                console.error('[Favorites] Error loading favorites:', error);
            } finally {
                setLoading(false);
            }
        };

        loadFavorites();
    }, []);

    // Estrai generi unici da tutti i preferiti
    const allGenres = React.useMemo(() => {
        const genreSet = new Set();
        favorites.forEach(artist => {
            if (artist.genres && Array.isArray(artist.genres)) {
                artist.genres.forEach(genre => genreSet.add(genre));
            }
        });
        return Array.from(genreSet).sort();
    }, [favorites]);

    // Filtra e ordina artisti (client-side per evitare "scatti")
    const filteredFavorites = React.useMemo(() => {
        let filtered = favorites;
        
        // Filtra per genere
        if (selectedGenre) {
            filtered = filtered.filter(artist => 
                artist.genres && artist.genres.includes(selectedGenre)
            );
        }
        
        // Ordina client-side
        const sorted = [...filtered].sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name, language === 'it' ? 'it' : 'en');
            } else {
                // Ordina per data (più recente prima)
                const dateA = new Date(a.saved_at);
                const dateB = new Date(b.saved_at);
                return dateB - dateA;
            }
        });
        
        return sorted;
    }, [favorites, selectedGenre, sortBy, language]);

    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Rimuovi dai preferiti
    const handleRemoveFavorite = async (artistId) => {
        try {
            await FavoritesService.removeFavorite(artistId);
            // Aggiorna lista locale
            setFavorites(prev => prev.filter(a => a.id !== artistId));
            // Aggiorna context
            await refreshFavorites();
            setShowRemoveConfirm(null);
        } catch (error) {
            console.error('[Favorites] Error removing favorite:', error);
        }
    };

    const openListenModal = (artist, mode = 'listen') => {
        setListenModal({
            isOpen: true,
            artistName: artist.name,
            ids: {
                deezerId: artist.deezer_id || null,
                spotifyId: artist.spotify_url
                    ? artist.spotify_url.split('/').pop()
                    : null,
            },
            mode,
        });
        setOpenMenuId(null);
    };

    const toggleMenu = (event, artistId) => {
        event.stopPropagation();
        setOpenMenuId(prev => (prev === artistId ? null : artistId));
    };

    const handleMenuRemove = (artistId) => {
        setShowRemoveConfirm(artistId);
        setOpenMenuId(null);
    };
    
    // Formatta data
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', options);
    };

    if (loading) {
        return null; // Non mostrare nulla durante il caricamento
    }

    if (favorites.length === 0) {
        return (
            <div className="favorites-container">
                <div className="favorites-empty">
                    <span className="material-icons">star_border</span>
                    <h2>{language === 'it' ? 'Nessun preferito' : 'No favorites'}</h2>
                    <p>{language === 'it' 
                        ? 'Inizia a salvare i tuoi artisti preferiti cliccando sulla stellina'
                        : 'Start saving your favorite artists by clicking the star'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="favorites-container">
            <div className="favorites-content">
                <div className="favorites-header-image">
                    <img src="/img/love.jpg" alt="Foundly" />
                </div>

                {/* Filtri generi e ordinamento */}
                <div className="favorites-controls">
                    {allGenres.length > 0 && (
                        <div className="favorites-filters">
                            <button
                                className={`genre-filter ${selectedGenre === null ? 'active' : ''}`}
                                onClick={() => setSelectedGenre(null)}
                            >
                                {language === 'it' ? 'Tutti' : 'All'}
                            </button>
                            {allGenres.map(genre => (
                                <button
                                    key={genre}
                                    className={`genre-filter ${selectedGenre === genre ? 'active' : ''}`}
                                    onClick={() => setSelectedGenre(genre)}
                                >
                                    {genre}
                                </button>
                            ))}
                        </div>
                    )}
                    
                    {/* Ordinamento */}
                    <div className="favorites-sort">
                        <button
                            className={`sort-button ${sortBy === 'date' ? 'active' : ''}`}
                            onClick={() => setSortBy('date')}
                        >
                            {language === 'it' ? 'Data' : 'Date'}
                        </button>
                        <button
                            className={`sort-button ${sortBy === 'name' ? 'active' : ''}`}
                            onClick={() => setSortBy('name')}
                        >
                            {language === 'it' ? 'Nome' : 'Name'}
                        </button>
                    </div>
                </div>

                {/* Lista artisti */}
                <div className="favorites-list">
                    {filteredFavorites.map(artist => (
                        <div key={artist.id} className="favorite-item">
                            {/* Immagine artista */}
                            <div className="favorite-image">
                                {artist.image_url ? (
                                    <img 
                                        src={artist.image_url} 
                                        alt={artist.name}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div 
                                    className="favorite-image-placeholder"
                                    style={{ display: artist.image_url ? 'none' : 'flex' }}
                                >
                                    <span className="material-icons">music_note</span>
                                </div>
                            </div>

                            {/* Info artista */}
                            <div className="favorite-info">
                                <h3 className="favorite-name">{artist.name}</h3>
                                {artist.genres && artist.genres.length > 0 && (
                                    <p className="favorite-genres">
                                        {artist.genres.slice(0, 3).join(', ')}
                                    </p>
                                )}
                                <p className="favorite-date">
                                    {language === 'it' ? 'Aggiunto il ' : 'Added on '}
                                    {formatDate(artist.saved_at)}
                                </p>
                            </div>

                            {/* Azioni */}
                            <div className="favorite-actions">
                                <button
                                    className="favorite-menu-btn"
                                    onClick={(event) => toggleMenu(event, artist.id)}
                                    aria-label={language === 'it' ? 'Azioni' : 'Actions'}
                                    title={language === 'it' ? 'Azioni' : 'Actions'}
                                >
                                    <span className="material-icons">more_vert</span>
                                </button>
                                {openMenuId === artist.id && (
                                    <div className="favorite-actions-menu" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => openListenModal(artist, 'listen')}
                                        >
                                            <span className="material-icons">headphones</span>
                                            {language === 'it' ? 'Ascolta su...' : 'Listen on...'}
                                        </button>
                                        <button
                                            onClick={() => openListenModal(artist, 'share')}
                                        >
                                            <span className="material-icons">share</span>
                                            {language === 'it' ? 'Condividi' : 'Share'}
                                        </button>
                                        <button
                                            className="danger"
                                            onClick={() => handleMenuRemove(artist.id)}
                                        >
                                            <span className="material-icons">delete</span>
                                            {language === 'it' ? 'Rimuovi dai preferiti' : 'Remove'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Messaggio se nessun risultato con filtro */}
                {filteredFavorites.length === 0 && selectedGenre && (
                    <div className="favorites-empty-filter">
                        <p>{language === 'it' 
                            ? `Nessun artista con genere "${selectedGenre}"`
                            : `No artists with genre "${selectedGenre}"`}</p>
                    </div>
                )}
            </div>

            {/* ListenModal — ascolta su / condividi */}
            <ListenModal
                isOpen={listenModal.isOpen}
                onClose={() => setListenModal({ isOpen: false, artistName: null, ids: {}, mode: 'listen' })}
                artistName={listenModal.artistName}
                ids={listenModal.ids}
                mode={listenModal.mode}
            />

            {/* Modal conferma rimozione */}
            {showRemoveConfirm && (
                <div className="remove-confirm-overlay" onClick={() => setShowRemoveConfirm(null)}>
                    <div className="remove-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{language === 'it' ? 'Rimuovere dai preferiti?' : 'Remove from favorites?'}</h3>
                        <p>{language === 'it' 
                            ? 'Sei sicuro di voler rimuovere questo artista dai tuoi preferiti?'
                            : 'Are you sure you want to remove this artist from your favorites?'}</p>
                        <div className="remove-confirm-buttons">
                            <button
                                className="remove-confirm-cancel"
                                onClick={() => setShowRemoveConfirm(null)}
                            >
                                {language === 'it' ? 'Annulla' : 'Cancel'}
                            </button>
                            <button
                                className="remove-confirm-ok"
                                onClick={() => handleRemoveFavorite(showRemoveConfirm)}
                            >
                                {language === 'it' ? 'Rimuovi' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Favorites;

